import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { AuthLayout } from "@/components/auth";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleGoogleCallback } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const processCallback = async () => {
      const exchangeCode = searchParams.get("exchange_code");
      const error = searchParams.get("error");

      if (error) {
        setStatus("error");
        setErrorMessage(error === "access_denied"
          ? "You denied access to Google. Please try again."
          : error === "email_not_verified"
          ? "Your Google email is not verified. Please verify it with Google and try again."
          : "Failed to authenticate with Google");
        toast({
          title: "Google login failed",
          description: errorMessage || "An error occurred during Google login",
          variant: "destructive",
        });
        return;
      }

      if (!exchangeCode) {
        setStatus("error");
        setErrorMessage("No authentication code received");
        toast({
          title: "Authentication failed",
          description: "No exchange code received from Google",
          variant: "destructive",
        });
        return;
      }

      try {
        // Redeem the one-time code for the access token + refresh cookie.
        await handleGoogleCallback(exchangeCode);
        toast({
          title: "Welcome!",
          description: "You have successfully logged in with Google.",
        });

        // Navigate to dashboard after successful authentication
        navigate("/app", { replace: true });
      } catch (err) {
        setStatus("error");
        setErrorMessage("Failed to process authentication");
        toast({
          title: "Authentication failed",
          description: "Could not complete Google login",
          variant: "destructive",
        });
      }
    };

    processCallback();
  }, [searchParams, handleGoogleCallback, navigate, toast, errorMessage]);

  if (status === "error") {
    return (
      <AuthLayout
        title="Authentication Failed"
        subtitle="We couldn't log you in with Google"
      >
        <div className="flex flex-col items-center justify-center py-8 space-y-6">
          <XCircle className="h-16 w-16 text-red-500" />
          <p className="text-center text-muted-foreground">
            {errorMessage}
          </p>
          <Button onClick={() => navigate("/login")} className="mt-4">
            Back to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Signing you in..."
      subtitle="Please wait while we complete your Google login"
    >
      <div className="flex flex-col items-center justify-center py-8 space-y-6">
        <Loader2 className="h-16 w-16 text-primary animate-spin" />
        <p className="text-center text-muted-foreground">
          Completing authentication...
        </p>
      </div>
    </AuthLayout>
  );
}

export default GoogleCallback;
